import { formatDateString } from "@/v2-common/utils/utils.date"
import Icon from "@/v2-ui/icon"
import { DotText } from "@/v2-backoffice-client/components/dot"
import Link from "@/v2-backoffice-client/components/link"
import { FENERUM_ACCOUNT_BASE_URL } from "@/v2-backoffice-client/constants"
import type { LicenseAccountFragment } from "@/v2-backoffice-client/license/account/__types__/license.account.fragment"

export type CustomerBillingAccountCardProps = {
  account: LicenseAccountFragment
}

// The customer's Fenerum account, as a fact card at the top of the Billing
// tab — presentational only; the tab owns the query. Same facts the Billing
// Accounts board shows for the row, minus the link-worklist columns (a
// linked account has no unmatched story).
function CustomerBillingAccountCard(props: CustomerBillingAccountCardProps) {
  const { account } = props

  const facts: { label: string; value: string | null }[] = [
    { label: "Org number", value: account.LegalVatNumber ?? null },
    { label: "Selling entity", value: account.SellingEntity ?? null },
    {
      label: "EAN invoicing",
      value:
        account.IsEanInvoicing == null
          ? null
          : account.IsEanInvoicing
            ? "Yes"
            : "No",
    },
    { label: "Language", value: account.LanguageCode ?? null },
    { label: "ERP id", value: account.ErpId ?? null },
    { label: "Fenerum code", value: account.ExternalAccountCode ?? null },
    {
      label: "Changed in Fenerum",
      value: account.ExternalLastModifiedAt
        ? formatDateString(account.ExternalLastModifiedAt)
        : null,
    },
    {
      label: "Synced",
      value: account.SyncedAt ? formatDateString(account.SyncedAt) : null,
    },
  ].filter((fact) => fact.value != null)

  return (
    <div className="flex flex-col gap-2 rounded-md border p-3">
      <div className="flex items-center gap-2">
        <Icon src="bank" size={16} className="shrink-0 text-muted-foreground" />
        <h3 className="text-sm font-semibold">
          {/* Push (>>): the account panel opens on top of the customer panel
              with a breadcrumb back. */}
          <Link
            variant="muted"
            iconSrc={null}
            to={`>>?v=billingAccount:${account.Id}`}
          >
            {account.CompanyName ?? "Fenerum account"}
          </Link>
        </h3>
        {account.IsArchived ? (
          <DotText variant="muted" className="text-xs">
            Archived
          </DotText>
        ) : (
          <DotText variant="success-1" className="text-xs">
            Linked
          </DotText>
        )}
      </div>

      {facts.length > 0 && (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-4">
          {facts.map((fact) => (
            <div key={fact.label} className="flex flex-col">
              <dt className="text-xs text-muted-foreground">{fact.label}</dt>
              <dd className="text-sm tabular-nums">{fact.value}</dd>
            </div>
          ))}
        </dl>
      )}

      <p className="text-xs text-muted-foreground">
        <a
          className="underline"
          href={`${FENERUM_ACCOUNT_BASE_URL}/${account.ExternalAccountUuid}/`}
          target="_blank"
          rel="noreferrer"
        >
          Open in Fenerum ({account.ExternalAccountUuid})
        </a>
      </p>
    </div>
  )
}

export default CustomerBillingAccountCard
